import React, { useState, useEffect } from "react";
import { getData, postData, putData, deleteData, patchData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import { useAuth } from "../../context/AuthContext";
import OrderFormModal from "./OrderFormModal";
import OrderDetailModal from "./OrderDetailModal";
import StatusChangeModal from "./StatusChangeModal";
import ProductionTableModal from "./ProductionTableModal";
import DetailedReportModal from "./DetailedReportModal";
import {
  FaShoppingCart,
  FaPlus,
  FaEdit,
  FaEye,
  FaSearch,
  FaFilter,
  FaClipboardList,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaFileExcel,
  FaCalendarAlt,
  FaTrash
} from "react-icons/fa";

function OrderManager() {
  const { user } = useAuth(); // Obtener usuario autenticado
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'edit', 'view'
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    order_id: '',
    seller_id: '',
    seller_dni: '',
    client_dni: '',
    search: '' // Búsqueda general (nombre, DNI, orden)
  });
  
  // Estados para modales
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProductionTable, setShowProductionTable] = useState(false);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [changingStatusOrder, setChangingStatusOrder] = useState(null);
  
  // Estados para ajuste masivo de fechas
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showDateAdjustModal, setShowDateAdjustModal] = useState(false);
  const [daysAdjustment, setDaysAdjustment] = useState(0);
  
  // Datos de referencia del admin
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentForms, setPaymentForms] = useState([]); // Formas de pago
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [ecommerceTypes, setEcommerceTypes] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [colors, setColors] = useState([]);
  const [accessories, setAccessories] = useState([]);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estado para edición inline de campos financieros
  const [editingCell, setEditingCell] = useState(null); // { orderId, field }
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null); // { orderId, field }

  useEffect(() => {
    loadReferenceData();
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReferenceData = async () => {
    try {
      // Cargar cada endpoint individualmente para mejor manejo de errores
      const apiCalls = [
        { name: 'clients', endpoint: '/api/admin/get_clients' },
        { name: 'users', endpoint: '/api/get_users' },
        { name: 'orderStatus', endpoint: '/api/admin/get_order_status' },
        { name: 'paymentMethods', endpoint: `/api/get_my_payment_methods?uid=${user?.id}` },
        { name: 'paymentForms', endpoint: '/api/get_payment_forms' },
        { name: 'deliveryTypes', endpoint: '/api/admin/get_delivery_types' },
        { name: 'ecommerceTypes', endpoint: '/api/admin/get_ecommerce_types' },
        { name: 'skus', endpoint: '/api/admin/get_sku_mains' },
        { name: 'materials', endpoint: '/api/admin/get_materials' },
        { name: 'colors', endpoint: '/api/admin/get_colors' },
        { name: 'accessories', endpoint: '/api/admin/get_accessories' }
      ];

      const results = {};
      
      // Cargar cada endpoint por separado para identificar cuál falla
      for (const call of apiCalls) {
        try {
          results[call.name] = await getData(call.endpoint, false); // No mostrar alertas
        } catch (error) {
          console.error(`Error cargando ${call.name}:`, error);
          results[call.name] = null; // Continuar con los demás aunque uno falle
        }
      }

      const [
        clientsRes,
        usersRes,
        statusesRes,
        paymentsRes,
        paymentFormsRes,
        deliveryRes,
        ecommerceRes,
        skusRes,
        materialsRes,
        colorsRes,
        accessoriesRes
      ] = [
        results.clients,
        results.users,
        results.orderStatus,
        results.paymentMethods,
        results.paymentForms,
        results.deliveryTypes,
        results.ecommerceTypes,
        results.skus,
        results.materials,
        results.colors,
        results.accessories
      ];

      // Procesar clientes - SOLO mostrar mayoristas (is_wholesale = 1)
      if (clientsRes?.data?.clients) {
        const wholesaleClients = clientsRes.data.clients.filter(c => c.is_wholesale === 1 || c.is_wholesale === true);
        setClients(wholesaleClients);
      }

      // Procesar vendedores (usuarios con rol de vendedor)
      if (usersRes?.data?.users) {
        const sellersData = usersRes.data.users.filter(u => u.active && u.user_rol === 2); // rol 2 = vendedor (campo user_rol)
        setSellers(sellersData);
      }

      // Procesar estados de orden
      if (statusesRes?.data) {
        const statusData = Array.isArray(statusesRes.data) ? statusesRes.data : statusesRes.data.orderStatuses || [];
        
        // Usar todos los estados si no tienen campo active, o filtrar por active
        const finalStatuses = statusData.filter(s => s.active !== false); // Incluir si active es undefined
        
        setOrderStatuses(finalStatuses.length > 0 ? finalStatuses : statusData);
      }

      // Procesar métodos de pago
      // /api/get_my_payment_methods ya devuelve solo los métodos asignados al usuario autenticado
      // (para vendedores: solo los suyos; para admin/otros: todos los activos)
      if (paymentsRes?.data) {
        const paymentData = Array.isArray(paymentsRes.data) ? paymentsRes.data : paymentsRes.data.paymentMethods || [];
        setPaymentMethods(paymentData);
      }

      // Procesar formas de pago
      if (paymentFormsRes?.data) {
        const formsData = Array.isArray(paymentFormsRes.data) ? paymentFormsRes.data : paymentFormsRes.data.paymentForms || [];
        setPaymentForms(formsData.filter(f => f.active));
      }

      // Procesar tipos de entrega
      if (deliveryRes?.data) {
        const deliveryData = Array.isArray(deliveryRes.data) ? deliveryRes.data : deliveryRes.data.deliveryTypes || [];
        setDeliveryTypes(deliveryData.filter(d => d.active));
      }

      // Procesar tipos de ecommerce
      if (ecommerceRes?.data) {
        const ecommerceData = Array.isArray(ecommerceRes.data) ? ecommerceRes.data : ecommerceRes.data.ecommerceTypes || [];
        setEcommerceTypes(ecommerceData.filter(e => e.active));
      }

      // Procesar SKUs
      if (skusRes?.data) {
        const skuData = Array.isArray(skusRes.data) ? skusRes.data : skusRes.data.skuMains || [];
        setSkus(skuData.filter(s => s.active));
      }

      // Procesar materiales
      if (materialsRes?.data?.materials) {
        setMaterials(materialsRes.data.materials.filter(m => m.active));
      }

      // Procesar colores
      if (colorsRes?.data?.colors) {
        setColors(colorsRes.data.colors.filter(c => c.active));
      }

      // Procesar accesorios
      if (accessoriesRes?.data?.accessories) {
        setAccessories(accessoriesRes.data.accessories.filter(a => a.active));
      }

    } catch (error) {
      console.error('Error cargando datos de referencia:', error);
    }
  };

  const loadOrders = async (filterParams = null) => {
    try {
      setLoading(true);
      
      let url = '/api/orders/list';
      let queryParams = [];
      
      // Si el usuario es vendedor (role_id === 2), filtrar automáticamente por su ID
      if (user?.role_id === 2) {
        queryParams.push(`seller_dni=${user.user_id}`);
      }
      
      if (filterParams) {
        if (filterParams.order_id) queryParams.push(`order_id=${filterParams.order_id}`);
        // Solo permitir filtros adicionales si NO es vendedor
        if (user?.role_id !== 2) {
          if (filterParams.seller_id) queryParams.push(`seller_id=${filterParams.seller_id}`);
          if (filterParams.seller_dni) queryParams.push(`seller_dni=${filterParams.seller_dni}`);
        }
        if (filterParams.client_dni) queryParams.push(`client_dni=${filterParams.client_dni}`);
      }
      
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }
      

      const response = await getData(url, false); // No mostrar alerta automáticamente

      if (response?.data?.orders) {
        setOrders(response.data.orders);
      } else {
        setOrders([]);
      }
      
    } catch (error) {
      console.error('Error cargando pedidos:', error);
      toastError('Error al cargar los pedidos');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => value.trim() !== '')
    );
    
    if (Object.keys(activeFilters).length === 0) {
      loadOrders();
    } else {
      loadOrders(activeFilters);
    }
  };

  const clearFilters = () => {
    setCurrentPage(1);
    setFilters({
      order_id: '',
      seller_id: '',
      seller_dni: '',
      client_dni: '',
      search: ''
    });
    loadOrders();
  };

  // Filtrar órdenes localmente según criterios de búsqueda
  const filteredOrders = orders.filter(order => {
    if (!filters.search.trim()) return true;
    
    const searchLower = filters.search.toLowerCase();
    
    // Buscar en número de pedido
    if (order.order_id?.toString().includes(searchLower)) return true;
    
    // Buscar en nombre del cliente
    if (order.client_name?.toLowerCase().includes(searchLower)) return true;
    
    // Buscar en DNI del cliente
    if (order.client_dni?.toLowerCase().includes(searchLower)) return true;
    
    return false;
  });

  // Paginación
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Helper para obtener descripción de forma de pago
  const getPaymentFormTitle = (paymentFormId) => {
    if (!paymentFormId) return 'N/A';
    const form = paymentForms.find(f => f.id === parseInt(paymentFormId));
    return form ? form.form_description : 'N/A';
  };

  // Helper para obtener descripción de medio de pago
  const getPaymentMethodTitle = (paymentMethodId) => {
    if (!paymentMethodId) return 'N/A';
    const method = paymentMethods.find(m => m.id === parseInt(paymentMethodId));
    return method ? method.payment_description : 'N/A';
  };

  const handleNewOrder = () => {
    setEditingOrder(null);
    setShowOrderModal(true);
  };

  const handleEditOrder = async (order) => {
    try {
      setLoading(true);
      
      // Obtener los detalles completos del pedido con estructura anidada completa (misma que handleViewOrder)
      const response = await getData(`/api/orders/get_detailed_orders?order_id=${order.order_id}`, true);
      
      if (response?.data?.orders && response.data.orders.length > 0) {
        const fullOrder = response.data.orders[0];

        if (fullOrder.items && fullOrder.items.length > 0) {

        }
        setEditingOrder(fullOrder);
        setShowOrderModal(true);
      } else {
        toastError('No se pudieron cargar los detalles del pedido');
      }
    } catch (error) {
      console.error('Error cargando detalles del pedido:', error);
      toastError('Error al cargar los detalles del pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    const confirmed = window.confirm(
      `⚠️ ADVERTENCIA: ¿Está seguro que desea eliminar el pedido #${order.order_id}?\n\n` +
      `Esta acción:\n` +
      `• Eliminará el pedido y todos sus items\n` +
      `• Eliminará todos los pagos asociados\n` +
      `• Eliminará el registro de comisiones\n` +
      `• NO SE PUEDE DESHACER\n\n` +
      `Cliente: ${order.client_name || 'N/A'}\n` +
      `Total: $${parseFloat(order.total || 0).toLocaleString('es-AR')}\n\n` +
      `¿Continuar con la eliminación?`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await deleteData(`/api/orders/${order.order_id}/delete`, null, true);
      if (response?.data?.success) {
        toastSuccess(`Pedido #${order.order_id} eliminado exitosamente`);
        await loadOrders();
      } else {
        toastError(response?.data?.message || 'Error al eliminar el pedido');
      }
    } catch (error) {
      console.error('Error eliminando pedido:', error);
      toastError('Error al eliminar el pedido');
    } finally {
      setLoading(false);
    }
  };

  // ─── Edición inline de campos financieros ───────────────────────────────────
  const FINANCIAL_FIELD_LABELS = {
    total: 'Total',
    credited_payment: 'Acreditado en caja',
    credit_balance: 'Cobrado p/cliente',
  };

  const handleFinancialCellClick = (order, field) => {
    setEditingCell({ orderId: order.order_id, field });
    setEditValue(parseFloat(order[field] || 0).toFixed(2));
  };

  const handleFinancialCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleFinancialCellSave = async (order, field) => {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < 0) {
      toastError('Ingrese un valor numérico válido (mayor o igual a 0)');
      return;
    }

    // No hay cambio real
    if (Math.abs(parsed - parseFloat(order[field] || 0)) < 0.001) {
      setEditingCell(null);
      setEditValue('');
      return;
    }

    setSavingCell({ orderId: order.order_id, field });
    setEditingCell(null);

    try {
      const response = await patchData(`/api/orders/${order.order_id}/financial`, {
        [field]: parsed,
      });

      if (response?.data?.success) {
        const { total, credited_payment, credit_balance, financial_cost } = response.data.data;
        // Actualizar el pedido en el estado local sin recargar toda la lista
        setOrders(prev => prev.map(o =>
          o.order_id === order.order_id
            ? { ...o, total, credited_payment, credit_balance, financial_cost }
            : o
        ));
        toastSuccess(`${FINANCIAL_FIELD_LABELS[field]} actualizado`);
      } else {
        toastError(response?.data?.msg || 'Error al guardar el valor');
      }
    } catch (error) {
      console.error('Error en ajuste financiero:', error);
      toastError('Error al guardar el valor financiero');
    } finally {
      setSavingCell(null);
      setEditValue('');
    }
  };

  const handleFinancialKeyDown = (e, order, field) => {
    if (e.key === 'Enter') handleFinancialCellSave(order, field);
    if (e.key === 'Escape') handleFinancialCellCancel();
  };

  // Renderiza una celda financiera editable inline
  const renderFinancialCell = (order, field, colorClass = '') => {
    const isEditing = editingCell?.orderId === order.order_id && editingCell?.field === field;
    const isSaving  = savingCell?.orderId  === order.order_id && savingCell?.field  === field;
    const value     = parseFloat(order[field] || 0);

    if (isSaving) {
      return (
        <td><span className="spinner-border spinner-border-sm text-secondary" title="Guardando…" /></td>
      );
    }

    if (isEditing) {
      return (
        <td style={{ padding: '2px 4px', minWidth: '110px' }}>
          <div className="d-flex align-items-center gap-1">
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control form-control-sm"
              style={{ width: '90px', padding: '2px 4px', fontSize: '0.8rem' }}
              value={editValue}
              autoFocus
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => handleFinancialKeyDown(e, order, field)}
              onBlur={() => handleFinancialCellSave(order, field)}
            />
          </div>
        </td>
      );
    }

    return (
      <td
        title={`Clic para editar ${FINANCIAL_FIELD_LABELS[field]}`}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => handleFinancialCellClick(order, field)}
      >
        <span className={colorClass || ''} style={{ borderBottom: '1px dashed #aaa' }}>
          {formatCurrency(value)}
        </span>
      </td>
    );
  };
  // ────────────────────────────────────────────────────────────────────────────

  const handleViewOrder = async (order) => {
    try {
      setLoading(true);
      
      // Obtener los detalles completos del pedido con estructura anidada completa
      const response = await getData(`/api/orders/get_detailed_orders?order_id=${order.order_id}`, true); // Sí mostrar alerta si falla
      
      if (response?.data?.orders && response.data.orders.length > 0) {
        const fullOrder = response.data.orders[0];
        
        // Calcular el total sumando todos los items (subtotal sin interés)
        const total = fullOrder.items?.reduce((sum, item) => {
          return sum + (item.quantity * item.sale_price);
        }, 0) || 0;
        
        // Preservar los campos de interés que vienen del backend
        const interestData = {
          interest_rate: fullOrder.interest_rate,
          interest_amount: fullOrder.interest_amount,
          total_with_interest: fullOrder.total_with_interest,
          installment_amount: fullOrder.installment_amount,
          payment_installments: fullOrder.payment_installments
        };
        
        // Actualizar solo el total, preservando los demás campos
        fullOrder.total = total;
        
        // Restaurar los campos de interés
        Object.assign(fullOrder, interestData);
        
        setViewingOrder(fullOrder);
        setShowDetailModal(true);
      } else {
        toastError('No se pudieron cargar los detalles del pedido');
      }
    } catch (error) {
      console.error('Error cargando detalles del pedido:', error);
      toastError('Error al cargar los detalles del pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setViewingOrder(null);
  };

  const handleChangeStatus = (order) => {
    setChangingStatusOrder(order);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setChangingStatusOrder(null);
  };

  const handleStatusChanged = (updatedOrder) => {
    // Actualizar el pedido en la lista
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.order_id === updatedOrder.order_id ? { ...order, ...updatedOrder } : order
      )
    );
    toastSuccess('Estado del pedido actualizado exitosamente');
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
    setEditingOrder(null);
  };

  const handleSaveOrder = async (orderData) => {
    try {
      // Validaciones básicas
      if (!orderData.items || orderData.items.length === 0) {
        throw new Error('Debe agregar al menos un ítem al pedido');
      }

      // Validar cliente según el modo
      if (!orderData.customer_id) {
        // Modo cliente nuevo - validar datos requeridos
        if (!orderData.customer_dni?.trim()) {
          throw new Error('DNI del cliente es requerido');
        }
        if (!orderData.customer_name?.trim()) {
          throw new Error('Nombre del cliente es requerido');
        }
        if (!orderData.customer_phone?.trim()) {
          throw new Error('Teléfono del cliente es requerido');
        }
      }

      // Validar dirección de entrega
      if (!orderData.customer_location?.trim()) {
        throw new Error('Dirección de entrega es requerida');
      }
      if (!orderData.customer_province?.trim()) {
        throw new Error('Provincia es requerida');
      }
      if (!orderData.customer_city?.trim()) {
        throw new Error('Ciudad es requerida');
      }

      // Validar campos de pedido
      if (!orderData.payment_method) {
        throw new Error('Método de pago es requerido');
      }
      if (!orderData.delivery_type) {
        throw new Error('Tipo de entrega es requerido');
      }
      if (!orderData.ecommerce_type) {
        throw new Error('Canal de venta es requerido');
      }

      // Validar cada ítem
      orderData.items.forEach((item, index) => {
        const hasSku = item.sku_id && item.sku_id !== '' && item.sku_id !== null;
        const hasStockProducts = item.stock_products && item.stock_products.length > 0;
        
        // Debe tener SKU O productos de stock
        if (!hasSku && !hasStockProducts) {
          throw new Error(`Ítem ${index + 1}: Se requiere SKU O al menos un producto de stock local`);
        }
        
        // Si tiene SKU, validar campos relacionados
        if (hasSku) {
          if (!item.quantity || item.quantity <= 0) {
            throw new Error(`Ítem ${index + 1}: Cantidad debe ser mayor a 0`);
          }
          if (!item.sale_price || item.sale_price <= 0) {
            throw new Error(`Ítem ${index + 1}: Precio debe ser mayor a 0`);
          }
          if (!item.details?.material_id) {
            throw new Error(`Ítem ${index + 1}: Material es requerido`);
          }
          if (!item.details?.measurements?.trim()) {
            throw new Error(`Ítem ${index + 1}: Medidas son requeridas`);
          }
        }
      });
      
      // Mapear los datos al formato esperado por la API
      const apiData = {
        data: {
          order: {
            // Cliente (null si es nuevo, o el ID si es existente)
            client_id: orderData.customer_id ? parseInt(orderData.customer_id) : null,
            
            // Datos de cliente nuevo (solo si client_id es null)
            customer_dni: orderData.customer_id ? null : orderData.customer_dni,
            customer_name: orderData.customer_id ? null : orderData.customer_name,
            customer_phone: orderData.customer_id ? null : orderData.customer_phone,
            customer_email: orderData.customer_id ? null : (orderData.customer_email || null),
            is_wholesale: orderData.customer_id ? null : (orderData.is_wholesale || false),
            
            // Dirección de entrega (siempre se envía)
            customer_location: orderData.customer_location,
            customer_province: orderData.customer_province,
            customer_city: orderData.customer_city,
            
            // Datos del pedido
            seller_id: user?.id || 1, // ID del usuario autenticado
            status: orderStatuses.find(s => s.status_description === 'PEDIR A TALLER')?.id || (orderStatuses.length > 0 ? orderStatuses[0].id : 1),
            ecommerce_type: parseInt(orderData.ecommerce_type),
            payment_method: parseInt(orderData.payment_method),
            payment_form: parseInt(orderData.payment_form),
            payment_installments: parseInt(orderData.payment_installments) || 0,
            // Campos de interés (si existen)
            interest_rate: orderData.interest_rate || null,
            interest_amount: orderData.interest_amount || null,
            total_with_interest: orderData.total_with_interest || null,
            installment_amount: orderData.installment_amount || null,
            credited_payment: 0, // Se inicializa en 0
            credit_balance: 0, // Se inicializa en 0
            observations: orderData.notes?.trim() || null,
            delivery_type: parseInt(orderData.delivery_type),
            estimated_delivery_days: (orderData.estimated_delivery_days !== null && orderData.estimated_delivery_days !== undefined) ? parseInt(orderData.estimated_delivery_days) : null,
            inserted_by: user?.id || 1 // ID del usuario autenticado
          },
          items: orderData.items.map(item => {
            const hasSku = item.sku_id && item.sku_id !== '' && item.sku_id !== null;
            
            return {
              sku_id: hasSku ? parseInt(item.sku_id) : null,
              quantity: item.quantity ? parseInt(item.quantity) : 0,
              sale_price: item.sale_price ? parseFloat(item.sale_price) : 0,
              details: item.details ? {
                material_id: item.details.material_id ? parseInt(item.details.material_id) : null,
                measurements: item.details.measurements ? item.details.measurements.trim() : null,
                accessory_id: item.details.accessory_id ? parseInt(item.details.accessory_id) : null,
                additional_accessories: item.details.additional_accessories || [],
                colors: item.details.colors || []
              } : {
                material_id: null,
                measurements: null,
                accessory_id: null,
                additional_accessories: [],
                colors: []
              },
              // Incluir productos de stock local si existen
              stock_products: (item.stock_products || []).map(sp => ({
                product_id: parseInt(sp.product_id),
                quantity: parseInt(sp.quantity),
                unit_price: parseFloat(sp.unit_price)
              }))
            };
          })
        }
      };

      // Enviar al backend
      const response = await postData('/api/orders/create_order', apiData.data);
            
      if (response && response.data) {
        toastSuccess('Pedido creado exitosamente');
        await loadOrders(); // Recargar la lista de pedidos
        return true;
      } else {
        throw new Error('No se recibió confirmación del servidor');
      }
    } catch (error) {
      console.error('Error al guardar pedido:', error);
      
      let errorMessage = 'Error al crear el pedido';
      if (error.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastError(errorMessage);
      throw error;
    }
  };

  const handleUpdateOrder = async (orderData) => {
    try {
      
      if (!editingOrder?.order_id) {
        throw new Error('No se puede actualizar: ID de orden no encontrado');
      }

      // Mismas validaciones que en crear
      if (!orderData.items || orderData.items.length === 0) {
        throw new Error('Debe agregar al menos un ítem al pedido');
      }

      // Validar campos de pedido
      if (!orderData.payment_method) {
        throw new Error('Método de pago es requerido');
      }
      if (!orderData.delivery_type) {
        throw new Error('Tipo de entrega es requerido');
      }
      if (!orderData.ecommerce_type) {
        throw new Error('Canal de venta es requerido');
      }

      // Validar cada ítem
      orderData.items.forEach((item, index) => {
        const hasSku = item.sku_id && item.sku_id !== '' && item.sku_id !== null;
        const hasStockProducts = item.stock_products && item.stock_products.length > 0;
        
        // Debe tener SKU O productos de stock
        if (!hasSku && !hasStockProducts) {
          throw new Error(`Ítem ${index + 1}: Se requiere SKU O al menos un producto de stock local`);
        }
        
        // Si tiene SKU, validar campos relacionados
        if (hasSku) {
          if (!item.quantity || item.quantity <= 0) {
            throw new Error(`Ítem ${index + 1}: Cantidad debe ser mayor a 0`);
          }
          if (!item.sale_price || item.sale_price <= 0) {
            throw new Error(`Ítem ${index + 1}: Precio debe ser mayor a 0`);
          }
          if (!item.details?.material_id) {
            throw new Error(`Ítem ${index + 1}: Material es requerido`);
          }
          if (!item.details?.measurements?.trim()) {
            throw new Error(`Ítem ${index + 1}: Medidas son requeridas`);
          }
        }
      });
      
      // Mapear los datos al formato esperado por la API de actualización
      const apiData = {
        order_id: editingOrder.order_id,
        order: {
          // NO se permite cambiar client_id en actualización
          client_id: editingOrder.client_id,
          seller_id: editingOrder.seller_id,
          status: parseInt(orderData.status) || editingOrder.status,
          ecommerce_type: parseInt(orderData.ecommerce_type),
          payment_form: orderData.payment_form ? parseInt(orderData.payment_form) : null,
          payment_method: parseInt(orderData.payment_method),
          payment_installments: parseInt(orderData.payment_installments) || 0,
          // Incluir datos de interés si existen
          interest_rate: orderData.interest_rate !== undefined ? parseFloat(orderData.interest_rate) : null,
          interest_amount: orderData.interest_amount !== undefined ? parseFloat(orderData.interest_amount) : null,
          total_with_interest: orderData.total_with_interest !== undefined ? parseFloat(orderData.total_with_interest) : null,
          installment_amount: orderData.installment_amount !== undefined ? parseFloat(orderData.installment_amount) : null,
          credited_payment: parseFloat(orderData.credited_payment) || editingOrder.credited_payment,
          credit_balance: parseFloat(orderData.credit_balance) || editingOrder.credit_balance,
          observations: orderData.notes?.trim() || null,
          delivery_type: parseInt(orderData.delivery_type),
          estimated_delivery_days: (orderData.estimated_delivery_days !== null && orderData.estimated_delivery_days !== undefined) ? parseInt(orderData.estimated_delivery_days) : null,
          updated_by: user?.id || user?.user_id || 1
        },
        items: orderData.items.map(item => {
          const hasSku = item.sku_id && item.sku_id !== '' && item.sku_id !== null;
          
  
          return {
            item_id: item.item_id || null, // Si existe item_id, se actualiza; si no, se crea nuevo
            sku_id: hasSku ? parseInt(item.sku_id) : null,
            quantity: item.quantity ? parseInt(item.quantity) : 0,
            sale_price: item.sale_price ? parseFloat(item.sale_price) : 0,
            details: item.details ? {
              material_id: item.details.material_id ? parseInt(item.details.material_id) : null,
              measurements: item.details.measurements ? item.details.measurements.trim() : null,
              accessory_id: item.details.accessory_id ? parseInt(item.details.accessory_id) : null,
              // Filtrar additional_accessories para evitar duplicados con accessory_id principal
              additional_accessories: (item.details.additional_accessories || []).filter(accId => 
                !item.details.accessory_id || parseInt(accId) !== parseInt(item.details.accessory_id)
              ),
              colors: item.details.colors || []
            } : {
              material_id: null,
              measurements: null,
              accessory_id: null,
              additional_accessories: [],
              colors: []
            },
            // Incluir productos de stock local si existen
            stock_products: (item.stock_products || []).map(sp => ({
              product_id: parseInt(sp.product_id),
              quantity: parseInt(sp.quantity),
              unit_price: parseFloat(sp.unit_price)
            }))
          };
        })
      };
      
      // Enviar al backend - apiData ya tiene la estructura correcta {order_id, order, items}
      const response = await putData('/api/orders/update_order', { data: apiData });
      
      
      if (response && response.data) {
        toastSuccess('Pedido actualizado exitosamente');
        await loadOrders(); // Recargar la lista de pedidos
        return true;
      } else {
        throw new Error('No se recibió confirmación del servidor');
      }
    } catch (error) {
      console.error('Error al actualizar pedido:', error);
      
      let errorMessage = 'Error al actualizar el pedido';
      if (error.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastError(errorMessage);
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusName = (statusId) => {
    if (statusId === null || statusId === undefined) return 'Estado desconocido';
    const status = orderStatuses.find(s => s.id?.toString() === statusId.toString());
    return status ? (status.status_description || status.status_name || status.name || 'Estado desconocido') : 'Estado desconocido';
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      1: 'bg-warning text-dark', // Pedido
      2: 'bg-info', // En Taller
      3: 'bg-primary', // Listo
      4: 'bg-success', // Entregado
      5: 'bg-danger' // Cancelado
    };
    return statusMap[status] || 'bg-secondary';
  };

  // ========== FUNCIONES PARA AJUSTE MASIVO DE FECHAS ==========
  
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAllOrders = () => {
    if (selectedOrders.length === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(order => order.order_id));
    }
  };

  const handleOpenDateAdjustModal = () => {
    if (selectedOrders.length === 0) {
      toastError('Debe seleccionar al menos un pedido');
      return;
    }
    setShowDateAdjustModal(true);
  };

  const handleCloseDateAdjustModal = () => {
    setShowDateAdjustModal(false);
    setDaysAdjustment(0);
  };

  const handleAdjustDates = async () => {
    if (selectedOrders.length === 0) {
      toastError('Debe seleccionar al menos un pedido');
      return;
    }

    if (daysAdjustment === 0) {
      toastError('Debe ingresar un valor diferente de 0');
      return;
    }

    try {
      setLoading(true);

      const dataToSend = {
        data: {
          order_ids: selectedOrders,
          days_adjustment: parseInt(daysAdjustment),
          user: user?.id || 1
        }
      };

      const response = await putData('/api/orders/adjust_delivery_dates', dataToSend);

      if (response?.data) {
        toastSuccess(`Fechas ajustadas exitosamente en ${selectedOrders.length} pedido(s)`);
        setShowDateAdjustModal(false);
        setSelectedOrders([]);
        setDaysAdjustment(0);
        await loadOrders();
      }
    } catch (error) {
      console.error('Error ajustando fechas:', error);
      toastError(error.response?.data?.msg || 'Error al ajustar las fechas de entrega');
    } finally {
      setLoading(false);
    }
  };

  const renderOrdersList = () => (
    <div className="orders-list">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <FaShoppingCart className="me-2 text-primary" />
              Lista de Pedidos
            </h5>
            <div className="d-flex gap-2">
              {/* Botón Ajustar Fechas */}
              {selectedOrders.length > 0 && (
                <button
                  className="btn btn-warning"
                  onClick={handleOpenDateAdjustModal}
                  disabled={loading}
                  title={`Ajustar fechas de ${selectedOrders.length} pedido(s) seleccionado(s)`}
                >
                  <FaCalendarAlt className="me-2" />
                  Ajustar Fechas ({selectedOrders.length})
                </button>
              )}
              {/* Botón Tabla de Producción - Solo para admin, finanzas y taller */}
              {user?.role_id !== 2 && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => setShowProductionTable(true)}
                    disabled={loading}
                    title="Ver tabla de producción completa"
                  >
                    <FaFileExcel className="me-2" />
                    Tabla de Producción
                  </button>
                  <button
                    className="btn btn-info"
                    onClick={() => setShowDetailedReport(true)}
                    disabled={loading}
                    title="Generar reporte detallado completo"
                  >
                    <FaFileExcel className="me-2" />
                    Excel Detalle
                  </button>
                </>
              )}
              <button
                className="btn btn-primary"
                onClick={handleNewOrder}
                disabled={loading}
              >
                <FaPlus className="me-2" />
                Nuevo Pedido
              </button>
            </div>
          </div>
          
          {/* Filtros */}
          <div className="row mt-3 g-2">
            {/* Búsqueda general */}
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por Nº Pedido, Nombre o DNI del Cliente"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
              <small className="text-muted">Busca en tiempo real</small>
            </div>

            {/* Filtros avanzados - Solo si no es vendedor */}
            {user?.role_id !== 2 && (
              <>
                <div className="col-md-3">
                  <select
                    className="form-select form-select-sm"
                    name="seller_id"
                    value={filters.seller_id}
                    onChange={handleFilterChange}
                  >
                    <option value="">Todos los vendedores</option>
                    {sellers.map(seller => (
                      <option key={seller.id} value={seller.id}>
                        {seller.username}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <div className="col-md-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="DNI Vendedor"
                    name="seller_dni"
                    value={filters.seller_dni}
                    onChange={handleFilterChange}
                  />
                </div> */}
              </>
            )}
            
            <div className="col-md-2">
              <div className="btn-group btn-group-sm w-100">
                <button
                  className="btn btn-outline-primary"
                  onClick={applyFilters}
                  disabled={loading}
                  title="Aplicar filtros"
                >
                  <FaFilter className="me-1" />
                  Aplicar
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={clearFilters}
                  disabled={loading}
                  title="Limpiar filtros"
                >
                  Limpiar
                </button>
              </div>
            </div>
          </div>
          
          {filters.search && (
            <div className="alert alert-info mt-2 py-2 mb-0">
              <small>
                <FaSearch className="me-1" />
                Mostrando {filteredOrders.length} de {orders.length} pedidos
                {filteredOrders.length > rowsPerPage && ` — página ${currentPage} de ${totalPages}`}
              </small>
            </div>
          )}
        </div>
        
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando pedidos...</div>
            </div>
          ) : (
            <>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={filteredOrders.length > 0 && selectedOrders.length === filteredOrders.length}
                        onChange={handleSelectAllOrders}
                        disabled={filteredOrders.length === 0}
                        title="Seleccionar todos"
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>Nº</th>
                    <th>Fecha</th>
                    <th>Días Entrega</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Estado</th>
                    <th>Total</th>
                    <th>Acreditado</th>
                    <th>Saldo</th>
                    <th>Forma/pago</th>
                    <th>Medio/pago</th>
                    <th title="Costo Financiero = Total − Acreditado − Cobrado">CF</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="text-center text-muted py-4">
                        {filters.search ? 'No se encontraron pedidos con ese criterio de búsqueda' : 'No hay pedidos registrados'}
                      </td>
                    </tr>
                  ) : (
                    paginatedOrders.map(order => (
                      <tr key={order.order_id}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedOrders.includes(order.order_id)}
                            onChange={() => handleSelectOrder(order.order_id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td>
                          <strong>#{order.order_id}</strong>
                        </td>
                        <td>
                          <small>
                            {new Date(order.inserted_date).toLocaleDateString('es-AR')}
                          </small>
                        </td>
                        <td>
                          <small className="text-primary fw-bold">
                            {order.estimated_delivery_days 
                              ? `${order.estimated_delivery_days} días`
                              : 'Sin estimar'}
                          </small>
                        </td>
                        <td>
                          <div>
                            <strong>{order.client_name}</strong>
                            <br />
                            <small className="text-muted">{order.client_dni}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{order.seller_name}</strong>
                          </div>
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm badge ${getStatusBadgeClass(order.status)} d-flex align-items-center gap-1`}
                            onClick={() => handleChangeStatus(order)}
                            title="Cambiar estado del pedido"
                            style={{ cursor: 'pointer', border: 'none' }}
                          >
                            {order.status_description}
                            <FaExchangeAlt size={10} />
                          </button>
                        </td>
                        {renderFinancialCell(order, 'total', 'fw-bold')}
                        {renderFinancialCell(order, 'credited_payment', 'text-success')}
                        {renderFinancialCell(order, 'credit_balance', order.credit_balance > 0 ? 'text-danger' : 'text-success')}
                        <td>
                          <small>{getPaymentFormTitle(order.payment_form)}</small>
                        </td>
                        <td>
                          <small>{getPaymentMethodTitle(order.payment_method)}</small>
                        </td>
                        <td title={`CF = Total − Acreditado − Cobrado`}>
                          <span className={parseFloat(order.financial_cost || 0) > 0 ? 'text-warning fw-bold' : 'text-muted'}>
                            {formatCurrency(order.financial_cost || 0)}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-info"
                              onClick={() => handleViewOrder(order)}
                              title="Ver detalle"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEditOrder(order)}
                              title="Editar pedido"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteOrder(order)}
                              title="Eliminar pedido"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {filteredOrders.length > 0 && (
              <div className="d-flex justify-content-between align-items-center px-1 pt-3 flex-wrap gap-2">
                {/* Selector filas por página */}
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">Filas por página:</small>
                  {[10, 20, 50].map(n => (
                    <button
                      key={n}
                      className={`btn btn-sm ${rowsPerPage === n ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => { setRowsPerPage(n); setCurrentPage(1); }}
                    >
                      {n}
                    </button>
                  ))}
                  <small className="text-muted ms-2">
                    {((currentPage - 1) * rowsPerPage) + 1}–{Math.min(currentPage * rowsPerPage, filteredOrders.length)} de {filteredOrders.length}
                  </small>
                </div>

                {/* Controles de página */}
                {totalPages > 1 && (
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(1)} title="Primera">&laquo;</button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Anterior</button>
                      </li>

                      {/* Números de página */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <li key={`ellipsis-${idx}`} className="page-item disabled">
                              <span className="page-link">&hellip;</span>
                            </li>
                          ) : (
                            <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                            </li>
                          )
                        )
                      }

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Siguiente</button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(totalPages)} title="Última">&raquo;</button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderCreateOrder = () => (
    <div className="create-order">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <FaPlus className="me-2 text-success" />
              Nuevo Pedido
            </h5>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setActiveTab('list')}
            >
              Volver a Lista
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            <FaExclamationTriangle className="me-2" />
            Funcionalidad movida al modal. Use el botón "Nuevo Pedido" en la lista.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="order-manager">
      {/* Navegación por pestañas */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
            type="button"
          >
            <FaClipboardList className="me-2" />
            Lista de Pedidos
          </button>
        </li>
        {viewingOrder && (
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'view' ? 'active' : ''}`}
              type="button"
            >
              <FaEye className="me-2" />
              Ver Pedido #{viewingOrder.order_id}
            </button>
          </li>
        )}
      </ul>

      {/* Contenido de las pestañas */}
      <div className="tab-content">
        {activeTab === 'list' && renderOrdersList()}
        {activeTab === 'view' && (
          <div className="alert alert-warning">
            <FaExclamationTriangle className="me-2" />
            La vista detallada estará disponible próximamente.
          </div>
        )}
      </div>

      {/* Modal de formulario de pedido */}
      <OrderFormModal
        isOpen={showOrderModal}
        onClose={handleCloseModal}
        onSave={editingOrder ? handleUpdateOrder : handleSaveOrder}
        order={editingOrder}
        clients={clients}
        skus={skus}
        materials={materials}
        colors={colors}
        accessories={accessories}
        orderStatuses={orderStatuses}
        paymentMethods={paymentMethods}
        paymentForms={paymentForms}
        deliveryTypes={deliveryTypes}
        ecommerceTypes={ecommerceTypes}
        isEditing={!!editingOrder}
      />

      {/* Modal de detalles del pedido */}
      <OrderDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetailModal}
        order={viewingOrder}
      />

      {/* Modal de cambio de estado */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={handleCloseStatusModal}
        order={changingStatusOrder}
        onStatusChanged={handleStatusChanged}
        paymentMethods={paymentMethods}
        paymentForms={paymentForms}
      />

      {/* Modal de tabla de producción */}
      <ProductionTableModal
        isOpen={showProductionTable}
        onClose={() => setShowProductionTable(false)}
      />

      {/* Modal de reporte detallado */}
      <DetailedReportModal
        isOpen={showDetailedReport}
        onClose={() => setShowDetailedReport(false)}
      />

      {/* Modal de ajuste masivo de fechas */}
      {showDateAdjustModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaCalendarAlt className="me-2 text-warning" />
                  Ajustar Días de Entrega
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDateAdjustModal}
                  disabled={loading}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Pedidos seleccionados:</strong> {selectedOrders.length}
                </div>
                
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Días a sumar/restar
                    <small className="text-muted ms-2">(Números negativos restan días)</small>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={daysAdjustment}
                    onChange={(e) => setDaysAdjustment(e.target.value)}
                    placeholder="Ej: 3 (suma 3 días) o -2 (resta 2 días)"
                    disabled={loading}
                  />
                </div>

                <div className="alert alert-warning">
                  <small>
                    <FaExclamationTriangle className="me-2" />
                    <strong>Nota:</strong> Esta acción modificará los días de entrega estimados de todos los pedidos seleccionados.
                    {daysAdjustment > 0 && ` Se sumarán ${daysAdjustment} día(s).`}
                    {daysAdjustment < 0 && ` Se restarán ${Math.abs(daysAdjustment)} día(s).`}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDateAdjustModal}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleAdjustDates}
                  disabled={loading || daysAdjustment === 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FaCalendarAlt className="me-2" />
                      Ajustar Días
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderManager;

